'use client';

import { parseGenderForAlternativeLink } from '@/utils/maps';
import { Skeleton } from '@mui/material';
import Link from '@/components/LocaleLink';
import _round from 'lodash/round';

import { generateAlternativeGenderUrl } from '@/utils/seo';
import { getChartType } from '@/utils/lm_utilities';
import useLocalePath from '@/hooks/useLocalePath';
import { getAgeDescAdjective } from '@/utils/lm_title_description_service';
import { useTranslation } from '@/contexts/TranslationContext';
import { formatCurrency } from '@/utils/formatLocale';

export const PageTitleSection = ({
  pageDataH1Title,
  pageDataH1SubTitle,
  hashOfLMMetaData,
  noindex,
  id,
  pageTitleDetails,
  urlUnit = '',
}) => {
  const { t } = useTranslation();
  return (
    <>
      {hashOfLMMetaData && pageDataH1Title && pageDataH1SubTitle ? (
        <>
          <h1>
            {pageDataH1Title}
            <span className="page-data-sup">{pageDataH1SubTitle}</span>
          </h1>
          <div
            className="d-flex justify-content-between align-items-center flex-wrap breadcrumb-wrapper"
            style={{ minHeight: '50px' }}
          >
            <nav aria-label={t('aria.breadcrumb')}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link prefetch={false} href="/">
                    {t('nav.home')}
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  {' '}
                  <Link prefetch={false} href={pageTitleDetails.breadcrumbLink1}>
                    {pageTitleDetails.breadcrumbValue1}
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  {' '}
                  <Link prefetch={false} href={pageTitleDetails.breadcrumbLink2}>
                    {pageTitleDetails.breadcrumbValue2}
                  </Link>
                </li>
                {pageTitleDetails.breadcrumbValue3 ? (
                  <li className="breadcrumb-item">
                    {' '}
                    <Link prefetch={false} href={pageTitleDetails.breadcrumbLink3}>
                      {pageTitleDetails.breadcrumbValueAge || id}
                    </Link>
                  </li>
                ) : (
                  <li className="breadcrumb-item active" aria-current="page">
                    {pageTitleDetails.breadcrumbValueAge || id}
                  </li>
                )}
                {pageTitleDetails.breadcrumbValue3 && (
                  <li className="breadcrumb-item active" aria-current="page">
                    {pageTitleDetails.breadcrumbValue3}
                  </li>
                )}
              </ol>
            </nav>
          </div>
        </>
      ) : (
        <>
          <Skeleton className="skeleton-bg" animation="wave" variant="rectangular" width={'100%'} height={48} />
          <Skeleton className="my-2 skeleton-bg" animation="wave" variant="rectangular" width={300} height={48} />
          <div className="d-flex">
            <Skeleton className="my-2 skeleton-bg" animation="wave" variant="rectangular" width={'50%'} height={40} />
            <Skeleton
              className="my-2 mx-3 skeleton-bg"
              animation="wave"
              variant="rectangular"
              width={'45%'}
              height={40}
            />
          </div>
        </>
      )}
    </>
  );
};

// Extract relative path from a URL that may be absolute.
function toRelativePath(url) {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

export const IdleCalulateText = ({
  className,
  blurbData,
  unit,
  hashArray,
  chartImage,
  chartImageAlt,
  urlUnit,
  ageInMonths,
  metaInfo,
}) => {
  const { t, locale } = useTranslation();
  const ageDesc = getAgeDescAdjective(hashArray[3], locale);
  const gender = hashArray[2]; // URL segment: "men" | "women" | "household"
  const chartType = getChartType(hashArray[1], locale);
  const altGenderSlug = parseGenderForAlternativeLink(hashArray, ageInMonths);
  const compareGenderLabel = t(
    altGenderSlug === 'women' ? 'form.women' :
    altGenderSlug === 'household' ? 'household.household' :
    'form.men'
  );

  const charts = metaInfo?.getChartDataSetByGenderAndAgeRes?.charts?.[0];
  const rangeLow = formatCurrency(
    _round(charts?.p_25 || charts?.p_10),
    locale,
  );
  const rangeHigh = formatCurrency(
    _round(charts?.p_75 || charts?.p_80),
    locale,
  );
  const median = formatCurrency(_round(charts?.median), locale);

  return (
    <div className={`${className ? className : 'info-text'} d-flex justify-content-between`}>
      <div className="chart-image d-none d-md-block">
        {chartImage ? (
          <img
            className="img"
            src={chartImage}
            alt={chartImageAlt}
            width={150}
            height={150}
            loading="eager"
            decoding="async"
            style={{ width: '150px', height: '150px', aspectRatio: 1 / 1, contain: 'strict' }}
          />
        ) : (
          <Skeleton className="mx-auto skeleton-bg" animation="wave" variant="rectangular" width={150} height={150} />
        )}
      </div>
      <div className="chart-summary-wrapper ms-2">
        {ageDesc ? (
          <h6 className="text-center ">
            {t('pageData.averageHeading', { chartType, ageDesc, gender })}
          </h6>
        ) : (
          <Skeleton className="mx-auto skeleton-bg" animation="wave" variant="rectangular" width={120} height={30} />
        )}
        {t('pageData.narrative', {
          ageDesc,
          gender,
          chartType,
          rangeLow,
          rangeHigh,
          median,
          link: (parts) => (
            <span key="nw-link" className="breadcrumb-item">
              <Link href="/">{parts}</Link>
            </span>
          ),
        })}
        <div className="d-flex justify-content-between mt-2">
          <div className="col chart-image d-block d-md-none ">
            {chartImage ? (
              <img
                className="img"
                src={chartImage}
                alt={chartImageAlt}
                width={100}
                height={100}
                loading="eager"
                decoding="async"
                style={{ width: '100px', height: '100px', aspectRatio: 1 / 1, contain: 'strict' }}
              />
            ) : (
              <Skeleton
                className="mx-auto skeleton-bg"
                animation="wave"
                variant="rectangular"
                width={150}
                height={150}
              />
            )}
          </div>
          {hashArray[2] !== 'household' ? (
            <p className="col breadcrumb-compare text-end text-muted ">
              {t('pageData.compareWith')}&nbsp;
              <span className="breadcrumb-item pl-1 pr-1">
                <Link href={toRelativePath(generateAlternativeGenderUrl(hashArray))}>
                  {t('pageData.compareLabel', {
                    ageDesc,
                    gender: compareGenderLabel,
                    chartType,
                    urlUnit: urlUnit || '',
                  })}
                </Link>
              </span>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
// export const IdleCalulateText = ({ className, blurbData, unit, hashArray, chartImage, chartImageAlt, urlUnit }) => {
//   const gender = parseUrlForGenderWithString(hashArray).toLocaleLowerCase();
//   const chartType = parseUrlForSimpleChartType(hashArray[1]);

//   const p2 = blurbData?.percentile_2;
//   const p3 = blurbData?.percentile_3;
//   const p50 = blurbData?.percentile_50;
//   const p97 = blurbData?.percentile_97;
//   const p98 = blurbData?.percentile_98;

//   const lowerBound = p3 || p2 || 0;
//   const upperBound = p97 || p98 || 0;
//   const midPoint = p50 || 0;

//   let rangeStart = '-';
//   let rangeEnd = '-';
//   let average = '-';

//   if (midPoint > 0 && lowerBound > 0 && upperBound > 0) {
//     if (unit === 'feet') {
//       const startFeetInch = convertCmToFeetInch(lowerBound);
//       const endFeetInch = convertCmToFeetInch(upperBound);
//       const avgFeetInch = convertCmToFeetInch(midPoint);

//       rangeStart = _round(startFeetInch.feet);
//       rangeEnd = `${_round(endFeetInch.feet)} feet ${_round(endFeetInch.inch)} inches`;
//       average = `${_round(avgFeetInch.feet)} feet and ${_round(avgFeetInch.inch)} inches`;
//     } else {
//       const unitText =
//         unit === 'inches-lbs'
//           ? 'inches and lbs'
//           : unit === 'cms-kgs'
//           ? 'cms and kgs'
//           : unit === 'feet-lbs'
//           ? 'feet and lbs'
//           : unit;

//       rangeStart = _round(lowerBound);
//       rangeEnd = `${_round(upperBound)} ${unitText}`;
//       average = `${_round(midPoint)} ${unitText}`;
//     }
//   }

//   const compareUnitText =
//     unit === 'inches-lbs'
//       ? 'inches and lbs'
//       : unit === 'cms-kgs'
//       ? 'cms and kgs'
//       : unit === 'feet-lbs'
//       ? 'feet and lbs'
//       : unit;

//   const altGenderUrl = generateAlternativeGenderUrl(hashArray);
//   const compareLink = `${altGenderUrl}/${urlUnit ?? ''}`;
//   const ageDesc = blurbData?.ageDesc || '';

//   return (
//     <div className={`${className ?? 'info-text'} d-flex justify-content-between`}>
//       <div className="chart-image d-none d-md-block">
//         {chartImage ? (
//           <img className="img" src={chartImage} alt={chartImageAlt} style={{ width: '150px', aspectRatio: 1 / 1 }} />
//         ) : (
//           <Skeleton
//             sx={{ bgcolor: 'rgba(186, 224, 218, 0.8)' }}
//             className="mx-auto"
//             animation="wave"
//             variant="rectangular"
//             width={150}
//             height={150}
//           />
//         )}
//       </div>

//       <div className="chart-summary-wrapper ms-2">
//         <h6 className="text-center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//           General Summary:&nbsp;
//           {ageDesc ? (
//             `${ageDesc} ${gender} ${chartType}`
//           ) : (
//             <span>
//               <Skeleton
//                 sx={{ display: 'inline-block', bgcolor: 'rgba(186, 224, 218, 0.8)' }}
//                 animation="wave"
//                 variant="text"
//                 width={130}
//                 height={24}
//                 className="mx-auto"
//               />
//             </span>
//           )}
//         </h6>

//         {/* <p>
//           In most cases, {chartType || '...'} measurements for {ageDesc || '...'} {gender || '...'} will be in the range
//           between {rangeStart || '...'} and {rangeEnd || '...'}. The average {chartType || '...'} for {ageDesc || '...'}{' '}
//           {gender || '...'} is {average || '...'}, according to the CDC and anonymized data from{' '}
//           <span className="breadcrumb-item">
//             <a href="/">Nettleworth.com</a>
//           </span>{' '}
//           users.
//         </p> */}
//         <p>
//           In most cases, {chartType || <InlineSkeleton width={80} />} measurements for{' '}
//           {ageDesc || <InlineSkeleton width={120} />} {gender || <InlineSkeleton />} will be in the range between{' '}
//           {rangeStart || <InlineSkeleton />} and {rangeEnd || <InlineSkeleton />}. The average{' '}
//           {chartType || <InlineSkeleton />} for {ageDesc || <InlineSkeleton />} {gender || <InlineSkeleton />} is{' '}
//           {average || <InlineSkeleton />}, according to the CDC and anonymized data from{' '}
//           <span className="breadcrumb-item">
//             <a href="/">Nettleworth.com</a>
//           </span>{' '}
//           users.
//         </p>

//         <div className="d-flex justify-content-between mt-2">
//           <div className="col chart-image d-block d-md-none">
//             {chartImage ? (
//               <img
//                 className="img"
//                 src={chartImage}
//                 alt={chartImageAlt}
//                 style={{ width: '100px', aspectRatio: 1 / 1 }}
//               />
//             ) : (
//               <Skeleton
//                 sx={{ bgcolor: 'rgba(186, 224, 218, 0.8)' }}
//                 className="mx-auto"
//                 animation="wave"
//                 variant="rectangular"
//                 width={100}
//                 height={100}
//               />
//             )}
//           </div>

//           <p className="col breadcrumb-compare text-end text-muted">
//             Compare with:&nbsp;
//             {ageDesc && chartType && compareUnitText ? (
//               <span className="breadcrumb-item pl-1 pr-1">
//                 <a href={compareLink}>
//                   {ageDesc || <InlineSkeleton />} {parseGenderForAlternativeLink(hashArray)}{' '}
//                   {chartType || <InlineSkeleton />}
//                   {urlUnit ? ` in ${compareUnitText}` : ''}
//                 </a>
//               </span>
//             ) : (
//               <InlineSkeleton width={90} />
//             )}
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

export const PageMetaContent = ({ content }) => {
  return (
    <>
      <div className="row">
        {content && content[0] === '<' && <div className="col" dangerouslySetInnerHTML={{ __html: content }} />}
      </div>
    </>
  );
};

export const SeeMorePages = ({ hashOfLMMetaData, noindex, urlUnit = '' }) => {
  const { t } = useTranslation();
  const formatRelativeUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.pathname;
    } catch (e) {
      // If already a relative path or invalid full URL, return as is
      return url.startsWith('http') ? '/' : url;
    }
  };

  return (
    <>
      {hashOfLMMetaData.previous || hashOfLMMetaData.next ? (
        <>
          <p className="text-center mb-0 ">{t('pageData.seeMoreAges')}</p>
          <ul className="pager" style={{ minHeight: '66px', minWidth: '300px' }}>
            {!hashOfLMMetaData.ageDesc.includes('15') && hashOfLMMetaData.previous && (
              <li className="previous my-3 my-lg-0">
                <Link href={formatRelativeUrl(hashOfLMMetaData.previous)}>{t('pageData.younger')}</Link>
              </li>
            )}
            {!hashOfLMMetaData.ageDesc.includes('100') && hashOfLMMetaData.next && (
              <li className="next my-3 my-lg-0">
                <Link href={formatRelativeUrl(hashOfLMMetaData.next)}>{t('pageData.older')}</Link>
              </li>
            )}
          </ul>
        </>
      ) : (
        <div className="d-flex gap-3 1">
          <Skeleton className="mb-4 skeleton-bg" animation="wave" variant="rectangular" width={80} height={40} />
          <Skeleton className="mb-4 skeleton-bg" animation="wave" variant="rectangular" width={80} height={40} />
        </div>
      )}
    </>
  );
};

const InlineSkeleton = ({ width = 40 }) => (
  <Skeleton
    variant="text"
    width={width}
    height={20}
    className="skeleton-bg"
    sx={{
      display: 'inline-block',
      verticalAlign: 'middle',
      borderRadius: '4px',
    }}
  />
);
